import React, { useState, useCallback, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { UploadIcon, X, FileIcon } from "lucide-react";
import { uploadFile } from "@/services/uploadService";

interface FileUploadManagerProps {
  onFilesChange: (
    files: string[],
    currentUploadingFiles: UploadingFile[]
  ) => void;
  existingPaths?: string[];
  uploadingFiles?: UploadingFile[];
  processId: string;
  phaseFormId: string;
}

export interface UploadingFile {
  file: File;
  fileName: string;
  fileSize: number;
  progress: number;
  status: "uploading" | "completed" | "error";
  path?: string;
}

export interface SerializedUploadingFile {
  fileName: string;
  fileSize: number;
  progress: number;
  status: "uploading" | "completed" | "error";
  path?: string;
}

export const FileUploadManager: React.FC<FileUploadManagerProps> = ({
  onFilesChange,
  existingPaths = [],
  uploadingFiles = [],
  processId,
  phaseFormId,
}) => {
  const [localUploadingFiles, setLocalUploadingFiles] = useState<UploadingFile[]>(
    uploadingFiles.map((file) => ({
      ...file,
      file: new File([], file.fileName, { type: "application/octet-stream" }),
    }))
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (uploadingFiles.length > 0) {
      setLocalUploadingFiles(uploadingFiles);
    }
  }, [uploadingFiles]);

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files) return;

      const newFiles = Array.from(files).map((file) => ({
        file,
        fileName: file.name,
        fileSize: file.size,
        progress: 0,
        status: "uploading" as const,
      }));

      const updatedFiles = [...localUploadingFiles, ...newFiles];
      setLocalUploadingFiles(updatedFiles);
      
      onFilesChange(existingPaths, updatedFiles);

      // Limpar o input para permitir selecionar o mesmo arquivo novamente
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      const uploadPromises = newFiles.map(async (uploadingFile) => {
        try {
          const response = await uploadFile(
            uploadingFile.file,
            {
              upload_origin: "FORM_DOCUMENT",
              params: {
                processId,
                phaseFormId,
              }
            },
            '/form/upload',
            (progress) => {
              setLocalUploadingFiles((prev) => {
                const updated = prev.map((f) =>
                  f.fileName === uploadingFile.fileName
                    ? { ...f, progress }
                    : f
                );
                onFilesChange(existingPaths, updated);
                return updated;
              });
            }
          );

          // O uploadFile já retorna { path, progress }, então usamos response.path
          const filePath = response.path;

          setLocalUploadingFiles((prev) => {
            const updated = prev.map((f) =>
              f.fileName === uploadingFile.fileName
                ? { ...f, status: "completed" as const, path: filePath, progress: 100 }
                : f
            );
            onFilesChange(
              [...existingPaths, filePath],
              updated
            );
            return updated;
          });

          return filePath;
        } catch (error) {
          console.error("Erro ao fazer upload do arquivo:", error);
          setLocalUploadingFiles((prev) => {
            const updated = prev.map((f) =>
              f.fileName === uploadingFile.fileName
                ? { ...f, status: "error" as const }
                : f
            );
            onFilesChange(existingPaths, updated);
            return updated;
          });
          return null;
        }
      });

      await Promise.all(uploadPromises);
    },
    [onFilesChange, existingPaths, localUploadingFiles, processId, phaseFormId]
  );

  const handleRemoveFile = useCallback(
    (fileToRemove: UploadingFile) => {
      const updatedFiles = localUploadingFiles.filter(
        (f) => f.fileName !== fileToRemove.fileName
      );
      setLocalUploadingFiles(updatedFiles);
      onFilesChange(
        existingPaths.filter((path) => path !== fileToRemove.path),
        updatedFiles
      );
    },
    [existingPaths, onFilesChange, localUploadingFiles]
  );

  return (
    <div className="w-full space-y-4">
      <input
        type="file"
        multiple
        onChange={handleFileSelect}
        className="hidden"
        ref={fileInputRef}
        id="file-upload-input"
      />
      
      <Button
        type="button"
        variant="ghost"
        className="w-full justify-center py-6 border border-background/30 text-background hover:bg-background/10 bg-transparent"
        onClick={() => fileInputRef.current?.click()}
      >
        <UploadIcon className="mr-2 h-5 w-5" />
        Selecionar arquivos
      </Button>
      
      <div className="text-xs text-grey-light">
        Você pode adicionar múltiplos arquivos. Formatos aceitos: PDF, DOC, DOCX, JPG, PNG, etc.
      </div>

      {/* Lista de arquivos */}
      {localUploadingFiles.length > 0 && (
        <div className="space-y-3">
          {localUploadingFiles.map((uploadingFile, index) => (
            <div 
              key={index} 
              className="flex items-start gap-3 p-3 border border-background/20 rounded-md bg-background/5"
            >
              <FileIcon className="h-8 w-8 text-background mt-1 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-background text-sm truncate">
                  {uploadingFile.fileName}
                </p>
                <p className="text-xs text-grey-light">
                  Tamanho: {(uploadingFile.fileSize / 1024).toFixed(1)} KB
                </p>
                
                {/* Status do upload */}
                <div className="mt-2">
                  {uploadingFile.status === 'uploading' && (
                    <>
                      <div className="text-xs text-blue-400 mb-1">
                        Enviando ({Math.round(uploadingFile.progress)}%)
                      </div>
                      <div className="w-full h-1.5 bg-background/20 rounded-full">
                        <div 
                          className="h-full bg-blue-400 rounded-full transition-all duration-300" 
                          style={{ width: `${uploadingFile.progress}%` }}
                        />
                      </div>
                    </>
                  )}
                  
                  {uploadingFile.status === 'completed' && (
                    <div className="text-xs text-green-400">
                      ✅ Enviado com sucesso
                    </div>
                  )}
                  
                  {uploadingFile.status === 'error' && (
                    <div className="text-xs text-red-400">
                      ❌ Erro ao enviar arquivo
                    </div>
                  )}
                </div>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveFile(uploadingFile)}
                className="text-grey-light hover:text-background flex-shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUploadManager;
